import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTenant } from '../Hooks/useTenant';
import { userService } from '../services/userService';

/**
 * Verifica se uma URL é externa (começa com http:// ou https://)
 * @param {string} url - URL a ser verificada
 * @returns {boolean} - true se for URL externa, false caso contrário
 */
const isExternalUrl = (url) => {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Componente que bloqueia rotas públicas quando authentication_type está como SSO
 * Redireciona para 404 se SSO estiver ativo e o usuário não estiver autenticado
 * 
 * @param {React.ReactNode} children - Componentes filhos a serem renderizados
 * @param {boolean} allowIfSSO - Se true, permite acesso mesmo quando SSO estiver ativo (para callbacks SSO)
 */
export const PublicRouteGuard = ({ children, allowIfSSO = false }) => {
  const { config } = useTenant();
  
  // Obtém o tipo de autenticação do nível raiz da configuração
  const authenticationType = config?.AUTHENTICATION_TYPE || null;
  
  const isSSO = authenticationType === 'SSO';
  const isAuthenticated = userService.isAuthenticated();
  
  // Calcula a URL de redirecionamento se necessário
  const shouldRedirect = isSSO && !allowIfSSO && !isAuthenticated;
  const logoutRedirect = config?.FEATURE_FLAGS?.logout_redirect;
  const redirectTo = shouldRedirect ? (logoutRedirect || '/404') : null;
  const isExternal = redirectTo && isExternalUrl(redirectTo);
  
  // Se for URL externa, usa window.location para redirecionar
  useEffect(() => {
    if (isExternal && redirectTo) {
      window.location.href = redirectTo;
    }
  }, [isExternal, redirectTo]);
  
  // Se SSO estiver ativo e a rota não permitir acesso mesmo com SSO
  // e o usuário não estiver autenticado, redireciona para logout_redirect ou 404
  if (shouldRedirect) {
    // Se for URL externa, retorna null (o useEffect fará o redirecionamento)
    if (isExternal) {
      return null;
    }
    
    // Se for URL interna, usa Navigate do React Router
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

